import { useCallback, useEffect, useState } from "react";
import { Eraser, FileDown, History, Loader2, Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { downloadGdprExport } from "@/components/ClientGdprPanel";

const fmtTs = (iso) => (iso ? `${formatDate(iso)} ${String(iso).slice(11, 16)}` : "—");
const cname = (c) => c.ragione_sociale || `${c.surname || ""} ${c.name || ""}`.trim() || "—";
const ACTIONS = { view: "Consultazione", update: "Modifica", delete: "Eliminazione", export: "Export dati", consents: "Consensi", anonymize: "Anonimizzazione", retention_keep: "Revisione conservazione" };
const Card = ({ title, children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={testid}>
    {title && <h2 className="text-base font-semibold text-slate-900 mb-3">{title}</h2>}
    {children}
  </div>
);

const PrivacyTab = ({ settings, reload }) => {
  const [text, setText] = useState(settings.privacy_text);
  const [newVer, setNewVer] = useState(true);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.put("/superadmin/gdpr/settings", { privacy_text: text, publish_new_version: newVer }); toast.success(newVer ? "Nuova versione pubblicata: tutti dovranno riaccettarla" : "Testo salvato"); reload(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-5">
      <Card title={`Informativa base piattaforma — versione ${settings.privacy_version}`} testid="gdpr-privacy-global">
        <p className="text-xs text-slate-500 mb-2">Pubblicata {settings.privacy_updated_at ? `il ${fmtTs(settings.privacy_updated_at)}` : "(testo predefinito)"} · {settings.acceptances_current} accettazioni della versione corrente.</p>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={14} className="text-sm font-mono" data-testid="gdpr-privacy-text" />
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={newVer} onCheckedChange={(v) => setNewVer(!!v)} data-testid="gdpr-privacy-new-version" /> Pubblica come nuova versione (richiede nuova accettazione)</label>
          <Button variant="ghost" size="sm" onClick={() => setText(settings.default_privacy_text)} data-testid="gdpr-privacy-reset">Testo generico</Button>
          <Button onClick={save} disabled={saving || !text.trim()} className="ml-auto" data-testid="gdpr-privacy-save">{saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Salva informativa</Button>
        </div>
      </Card>
      <AgencyPrivacyList baseText={settings.privacy_text} />
    </div>
  );
};

const AgencyPrivacyList = ({ baseText }) => {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/superadmin/gdpr/agency-privacy").then(({ data }) => setRows(data)), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { await api.put(`/superadmin/gdpr/agency-privacy/${edit.agency_id}`, { text: edit.text, enabled: edit.enabled, publish_new_version: true }); toast.success("Informativa agenzia salvata"); setEdit(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card title="Versione personalizzata per agenzia" testid="gdpr-agency-privacy">
      <p className="text-xs text-slate-500 mb-3">Se attiva, gli utenti e i clienti dell&apos;agenzia vedono questo testo al posto di quello base.</p>
      {!rows ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
        <div className="divide-y">
          {rows.map((r) => (
            <div key={r.agency_id} className="flex items-center justify-between py-2 text-sm">
              <span>{r.name}</span>
              <span className="flex items-center gap-3">
                <span className={`text-xs ${r.custom?.enabled ? "text-emerald-700 font-semibold" : "text-slate-400"}`}>{r.custom?.enabled ? `Personalizzata v${r.custom.version}` : "Usa testo base"}</span>
                <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`gdpr-agency-edit-${r.agency_id}`}
                  onClick={() => setEdit({ agency_id: r.agency_id, name: r.name, text: r.custom?.text || baseText, enabled: r.custom ? !!r.custom.enabled : true })}>Personalizza</Button>
              </span>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl" data-testid="gdpr-agency-dialog">
          <DialogHeader><DialogTitle>Informativa — {edit?.name}</DialogTitle></DialogHeader>
          {edit && (
            <>
              <Textarea value={edit.text} onChange={(e) => setEdit({ ...edit, text: e.target.value })} rows={14} className="text-sm font-mono" data-testid="gdpr-agency-text" />
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={edit.enabled} onCheckedChange={(v) => setEdit({ ...edit, enabled: !!v })} data-testid="gdpr-agency-enabled" /> Usa questa versione per l&apos;agenzia</label>
            </>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Annulla</Button><Button onClick={save} data-testid="gdpr-agency-save">Salva e pubblica</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const LogsTab = () => {
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    setRows(null);
    api.get("/superadmin/gdpr/access-logs", { params: { q: q || undefined, action: action || undefined } }).then(({ data }) => setRows(data)).catch((e) => { toast.error(formatApiError(e)); setRows([]); });
  }, [q, action]);
  useEffect(() => { load(); }, [action]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Card testid="gdpr-logs">
      <div className="flex flex-wrap gap-2 mb-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cerca cliente o utente…" className="w-72 h-9" data-testid="gdpr-logs-search" />
        <select value={action} onChange={(e) => setAction(e.target.value)} className="h-9 border rounded-md px-2 text-sm" data-testid="gdpr-logs-action">
          <option value="">Tutte le operazioni</option>
          {Object.entries(ACTIONS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <Button variant="outline" className="h-9" onClick={load} data-testid="gdpr-logs-refresh"><Search className="h-4 w-4 mr-1" />Cerca</Button>
      </div>
      {!rows ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
        <div className="overflow-x-auto max-h-[60vh]">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase sticky top-0 bg-white"><tr><th className="text-left p-2">Data e ora</th><th className="text-left p-2">Utente</th><th className="text-left p-2">Operazione</th><th className="text-left p-2">Cliente</th><th className="text-left p-2">Dettaglio</th></tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400">Nessun accesso registrato.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`gdpr-log-${r.id}`}>
                  <td className="p-2 tabular-nums whitespace-nowrap">{fmtTs(r.at)}</td>
                  <td className="p-2">{r.user_name} <span className="text-[10px] text-slate-400">{r.user_role}</span></td>
                  <td className="p-2"><span className={`text-xs font-semibold ${r.action === "view" ? "text-slate-600" : r.action === "anonymize" || r.action === "delete" ? "text-red-700" : "text-blue-700"}`}>{ACTIONS[r.action] || r.action}</span></td>
                  <td className="p-2">{r.client_name || r.client_id}</td>
                  <td className="p-2 text-xs text-slate-500 max-w-md truncate" title={r.detail || ""}>{r.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

const AnonymizeDialog = ({ target, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); setConfirm(""); }, [target]);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/superadmin/gdpr/clients/${target.id}/anonymize`, { reason, confirm }); toast.success(`Cliente anonimizzato (${data.client_ref})`); onDone(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="gdpr-anonymize-dialog">
        <DialogHeader><DialogTitle className="text-red-700 flex items-center gap-2"><Eraser className="h-5 w-5" /> Diritto all&apos;oblio — {target && cname(target)}</DialogTitle></DialogHeader>
        <div className="text-xs bg-red-50 border border-red-200 rounded p-2 text-red-800">Operazione irreversibile: anagrafica, contatti, documenti personali, messaggi e accesso all&apos;area riservata vengono eliminati o sostituiti. Polizze, preventivi e incassi restano senza dati personali per gli obblighi fiscali e IVASS.</div>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Motivo / riferimento della richiesta (es. PEC del 12/10/2026)" data-testid="gdpr-anonymize-reason" />
        <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Scrivi ANONIMIZZA per confermare" data-testid="gdpr-anonymize-confirm" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annulla</Button>
          <Button className="bg-red-600 hover:bg-red-700" onClick={run} disabled={busy || !reason.trim() || confirm.trim().toUpperCase() !== "ANONIMIZZA"} data-testid="gdpr-anonymize-run">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Anonimizza</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ClientActions = ({ c, onAnon, extra }) => (
  <span className="flex gap-1.5 justify-end">
    {extra}
    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadGdprExport(c.id, "pdf")} data-testid={`gdpr-export-pdf-${c.id}`}><FileDown className="h-3.5 w-3.5 mr-1" />PDF</Button>
    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadGdprExport(c.id, "csv")} data-testid={`gdpr-export-csv-${c.id}`}><FileDown className="h-3.5 w-3.5 mr-1" />CSV</Button>
    {!c.gdpr_anonymized && <Button size="sm" className="h-7 text-xs bg-red-600 hover:bg-red-700" onClick={() => onAnon(c)} data-testid={`gdpr-anonymize-${c.id}`}><Eraser className="h-3.5 w-3.5 mr-1" />Oblio</Button>}
  </span>
);

const ErasureTab = () => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [anon, setAnon] = useState(null);
  const [log, setLog] = useState([]);
  const loadLog = useCallback(() => api.get("/superadmin/gdpr/erasure-log").then(({ data }) => setLog(data)), []);
  useEffect(() => { loadLog(); }, [loadLog]);
  const search = () => api.get("/superadmin/gdpr/clients", { params: { q } }).then(({ data }) => setRes(data)).catch((e) => toast.error(formatApiError(e)));
  return (
    <div className="space-y-5">
      <Card title="Cerca il cliente che ha fatto richiesta" testid="gdpr-erasure-search">
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Nome, cognome, codice fiscale, P.IVA o email" className="max-w-md h-9" data-testid="gdpr-erasure-q" />
          <Button className="h-9" onClick={search} disabled={q.trim().length < 2} data-testid="gdpr-erasure-search-btn"><Search className="h-4 w-4 mr-1" />Cerca</Button>
        </div>
        {res && (
          <div className="divide-y mt-3">
            {res.length === 0 && <div className="text-sm text-slate-400 py-3">Nessun cliente trovato.</div>}
            {res.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`gdpr-client-${c.id}`}>
                <span><b>{cname(c)}</b> <span className="text-xs text-slate-500">{c.fiscal_code || c.email || ""} · {c.agency_name}</span>{c.gdpr_anonymized && <span className="ml-2 text-[10px] font-bold text-red-700">ANONIMIZZATO</span>}</span>
                <ClientActions c={c} onAnon={setAnon} />
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card title="Registro operazioni di oblio" testid="gdpr-erasure-log">
        {log.length === 0 ? <div className="text-sm text-slate-400">Nessuna operazione.</div> : log.map((l) => (
          <div key={l.id} className="text-sm border-t py-2" data-testid={`gdpr-erasure-${l.id}`}>
            <b>{fmtTs(l.at)}</b> · {l.client_ref} · da {l.by} · <span className="text-slate-600">{l.reason}</span>
            <div className="text-[11px] text-slate-500">{Object.entries(l.counts || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
          </div>
        ))}
      </Card>
      <AnonymizeDialog target={anon} onClose={() => setAnon(null)} onDone={() => { loadLog(); if (q.trim().length >= 2) search(); }} />
    </div>
  );
};

const RetentionTab = ({ settings, reload }) => {
  const [years, setYears] = useState(settings.retention_years);
  const [data, setData] = useState(null);
  const [anon, setAnon] = useState(null);
  const load = useCallback(() => { setData(null); api.get("/superadmin/gdpr/retention-review").then(({ data: d }) => setData(d)); }, []);
  useEffect(() => { load(); }, [load]);
  const saveYears = async () => {
    try { await api.put("/superadmin/gdpr/settings", { retention_years: Number(years) }); toast.success("Periodo di conservazione salvato"); reload(); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const keep = async (c) => {
    try { await api.post(`/superadmin/gdpr/retention-review/${c.id}/keep`); setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== c.id), total: d.total - 1 })); toast.success("Conservazione confermata per 12 mesi"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-5">
      <Card title="Periodo di conservazione" testid="gdpr-retention-settings">
        <div className="flex items-center gap-2 text-sm">Segnala per revisione i clienti senza attività da più di
          <Input type="number" min={1} max={30} value={years} onChange={(e) => setYears(e.target.value)} className="w-20 h-9" data-testid="gdpr-retention-years" /> anni
          <Button className="h-9" onClick={saveYears} data-testid="gdpr-retention-save">Salva</Button>
        </div>
        <p className="text-xs text-slate-500 mt-1">Attività = scadenza dell&apos;ultima polizza, oppure ultima modifica del cliente se non ha polizze. Nessuna cancellazione automatica.</p>
      </Card>
      <Card title={data ? `Da rivedere: ${data.total} clienti (inattivi dal ${formatDate(data.cutoff)})` : "Da rivedere"} testid="gdpr-retention-list">
        {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : data.items.length === 0 ? <div className="text-sm text-slate-400">Nessun cliente oltre il periodo di conservazione.</div> : (
          <div className="divide-y max-h-[60vh] overflow-y-auto">
            {data.items.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`gdpr-retention-${c.id}`}>
                <span><b>{cname(c)}</b> <span className="text-xs text-slate-500">{c.fiscal_code || ""} · {c.agency_name} · ultima attività {formatDate(c.last_activity)}</span></span>
                <ClientActions c={c} onAnon={setAnon} extra={<Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => keep(c)} data-testid={`gdpr-retention-keep-${c.id}`}>Conserva</Button>} />
              </div>
            ))}
          </div>
        )}
      </Card>
      <AnonymizeDialog target={anon} onClose={() => setAnon(null)} onDone={load} />
    </div>
  );
};

const CookieTab = ({ settings, reload }) => {
  const [text, setText] = useState(settings.cookie_text);
  const save = async () => {
    try { await api.put("/superadmin/gdpr/settings", { cookie_text: text }); toast.success("Testo banner cookie salvato"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card title="Banner cookie (area clienti e portale professionisti)" testid="gdpr-cookie">
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} className="text-sm" data-testid="gdpr-cookie-text" />
      <div className="flex justify-end mt-3"><Button onClick={save} disabled={!text.trim()} data-testid="gdpr-cookie-save">Salva</Button></div>
    </Card>
  );
};

export default function SuperAdminGdpr() {
  const [settings, setSettings] = useState(null);
  const reload = useCallback(() => api.get("/superadmin/gdpr/settings").then(({ data }) => setSettings(data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { reload(); }, [reload]);
  return (
    <div className="space-y-6" data-testid="superadmin-gdpr-page">
      <div>
        <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3"><ShieldCheck className="h-9 w-9 text-emerald-600" /> Privacy &amp; GDPR</h1>
        <p className="text-sm text-slate-500 mt-1">Informativa, log accessi, diritto all&apos;oblio, conservazione dati e cookie (Reg. UE 2016/679).</p>
      </div>
      {!settings ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : (
        <Tabs defaultValue="privacy">
          <TabsList>
            <TabsTrigger value="privacy" data-testid="gdpr-tab-privacy">Informativa</TabsTrigger>
            <TabsTrigger value="logs" data-testid="gdpr-tab-logs"><History className="h-4 w-4 mr-1" />Log accessi</TabsTrigger>
            <TabsTrigger value="erasure" data-testid="gdpr-tab-erasure">Diritto all&apos;oblio</TabsTrigger>
            <TabsTrigger value="retention" data-testid="gdpr-tab-retention">Conservazione dati</TabsTrigger>
            <TabsTrigger value="cookie" data-testid="gdpr-tab-cookie">Cookie</TabsTrigger>
          </TabsList>
          <TabsContent value="privacy"><PrivacyTab settings={settings} reload={reload} /></TabsContent>
          <TabsContent value="logs"><LogsTab /></TabsContent>
          <TabsContent value="erasure"><ErasureTab /></TabsContent>
          <TabsContent value="retention"><RetentionTab settings={settings} reload={reload} /></TabsContent>
          <TabsContent value="cookie"><CookieTab settings={settings} reload={reload} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
